import { isFluxModel } from './flux-rules';
import { languageKeyOf } from './languages-config';

/**
 * One language of `GET /voice_ai/v2/languages` (AUTO-6230), reached through
 * the `getVoiceAiLanguages` Hasura action. Declared here rather than taken from
 * the generated types so this module stays pure and unit-testable; the
 * generated type is structurally the same with every field nullable.
 */
export type LanguageCatalogEntry = {
  language: string;
  name: string;
  default_locale: string;
  stt_providers: string[];
  locales: Array<{ locale: string; stt_providers: string[] }>;
};

export type LocaleOption = {
  locale: string;
  language: string;
  label: string;
  /** No Deepgram model can transcribe it. */
  googleOnly: boolean;
  sttProviders: string[];
};

export type LocaleUnavailableReason =
  | 'google-only'
  | 'english-only-model'
  | 'language-taken';

export type LocaleAvailability =
  | { enabled: true }
  | { enabled: false; reason: LocaleUnavailableReason };

export type SpeechRecognition = {
  transcriptionProvider: string;
  speechModel: string;
};

const regionNames = new Intl.DisplayNames(['en'], {
  type: 'region',
  style: 'short',
});

const regionOf = (locale: string): string => {
  const region = locale.split('-')[1];
  return region ? regionNames.of(region.toUpperCase()) || region : '';
};

const titleCase = (value: string): string =>
  value.replace(/(^|\s)\S/g, (char) => char.toUpperCase());

/**
 * Every locale in the catalog, in catalog order. The region is shown only when
 * the language has more than one locale — "Portuguese (Brazil)" next to
 * "Portuguese (Portugal)", but plain "Korean".
 */
export const localeOptions = (
  catalog: LanguageCatalogEntry[]
): LocaleOption[] =>
  catalog.flatMap((language) =>
    language.locales.map(({ locale, stt_providers }) => ({
      locale,
      language: language.language,
      label:
        language.locales.length > 1
          ? `${language.name} (${regionOf(locale)})`
          : language.name,
      googleOnly: !stt_providers.includes('Deepgram'),
      sttProviders: stt_providers,
    }))
  );

/**
 * Whether a locale can be added under the channel's speech recognition.
 *
 * The STT provider and model are channel-wide, so a locale they cannot serve
 * must not be offered: Engagement rejects the whole overlay for one such entry
 * and the call silently drops back to a single language
 * (`flux_unsupported_language_code`). Deepgram models other than Flux are
 * English-only and cannot switch mid-call.
 *
 * `takenLanguages` are the keys already on the channel — one entry per key.
 * The STT reason wins over the taken one because it is the one the user can
 * act on by changing the channel, not the entry.
 */
export const localeAvailability = (
  option: Pick<LocaleOption, 'language' | 'sttProviders'>,
  { transcriptionProvider, speechModel }: SpeechRecognition,
  takenLanguages: string[]
): LocaleAvailability => {
  if (transcriptionProvider === 'Deepgram') {
    if (!isFluxModel(speechModel)) {
      if (option.language !== 'en') {
        return { enabled: false, reason: 'english-only-model' };
      }
    } else if (!option.sttProviders.includes('Deepgram')) {
      return { enabled: false, reason: 'google-only' };
    }
  } else if (
    transcriptionProvider === 'Google' &&
    !option.sttProviders.includes('Google')
  ) {
    return { enabled: false, reason: 'google-only' };
  }

  if (takenLanguages.includes(option.language)) {
    return { enabled: false, reason: 'language-taken' };
  }

  return { enabled: true };
};

/**
 * The header of an entry: `Language (Accent)` when an accent narrows its
 * voices, as in the mock, otherwise the locale's own label. Falls back to the
 * raw locale while the catalog is loading or for a locale it no longer lists.
 */
export const entryLabel = (
  entry: { locale: string; accent?: string },
  catalog: LanguageCatalogEntry[]
): string => {
  const option = localeOptions(catalog).find((o) => o.locale === entry.locale);
  if (!option) return entry.locale;
  if (!entry.accent) return option.label;

  const language = catalog.find(
    (l) => l.language === languageKeyOf(entry.locale)
  );
  return `${language?.name || option.label} (${titleCase(entry.accent)})`;
};

type Nullable<T> = T | null | undefined;

/** The `getVoiceAiLanguages` payload as codegen types it: every field nullable. */
export type RawLanguagesCatalog = Nullable<
  Array<
    Nullable<{
      language?: Nullable<string>;
      name?: Nullable<string>;
      default_locale?: Nullable<string>;
      stt_providers?: Nullable<Array<Nullable<string>>>;
      locales?: Nullable<
        Array<
          Nullable<{
            locale?: Nullable<string>;
            stt_providers?: Nullable<Array<Nullable<string>>>;
          }>
        >
      >;
    }>
  >
>;

const strings = (values: Nullable<Array<Nullable<string>>>): string[] =>
  (values || []).filter((v): v is string => typeof v === 'string' && !!v);

/**
 * Narrows the generated, all-nullable catalog to what the UI can offer: a
 * language needs a key and at least one locale, a locale needs its code.
 */
export const normalizeLanguagesCatalog = (
  raw: RawLanguagesCatalog
): LanguageCatalogEntry[] =>
  (raw || []).flatMap((language) => {
    if (!language?.language) return [];
    const locales = (language.locales || []).flatMap((l) =>
      l?.locale
        ? [{ locale: l.locale, stt_providers: strings(l.stt_providers) }]
        : []
    );
    if (locales.length === 0) return [];
    return [
      {
        language: language.language,
        name: language.name || language.language,
        default_locale: language.default_locale || locales[0].locale,
        stt_providers: strings(language.stt_providers),
        locales,
      },
    ];
  });

/**
 * ElevenLabs voice names carry a description ("Mateo - Warm, Friendly"); the
 * preview card and the collapsed entry header show only the name, as the mock
 * does. A hyphen inside a name ("Jean-Luc") is kept: only " - " separates.
 */
export const shortVoiceName = (name: string): string =>
  name.split(/\s+-\s+|,/)[0].trim() || name;

/**
 * The catalog option a stored code refers to. Engagement normalises codes, so
 * a code in any casing, or a bare language tag ("es"), is valid there and must
 * resolve here too — a bare tag to that language's default locale.
 */
export const findLocaleOption = (
  options: LocaleOption[],
  catalog: LanguageCatalogEntry[],
  code: string
): LocaleOption | undefined => {
  const wanted = code.toLowerCase();
  const exact = options.find((o) => o.locale.toLowerCase() === wanted);
  if (exact || wanted.includes('-')) return exact;
  const language = catalog.find((l) => l.language === wanted);
  return language
    ? options.find((o) => o.locale === language.default_locale)
    : undefined;
};
